import React, { useMemo } from 'react';
import type { MessageComposerState, TranslationLanguages } from 'stream-chat';
import { Attachment as DefaultAttachment, renderText as defaultRenderText, Poll, MessageContextValue, useChatContext, useComponentContext, useTranslationContext, useMessageComposer, useStateStore, CloseIcon } from 'stream-chat-react';
import { useAuth } from '../../../hooks/useAuth';
import clsx from 'clsx';
import { authorColor } from '../../../utils/Utils';

const messageComposerStateStoreSelector = (state: MessageComposerState) => ({
  quotedMessage: state.quotedMessage,
});

export type CustomQuotedMessagePreviewProps = {
  renderText?: MessageContextValue['renderText'];
};

export const CustomQuotedMessagePreview = ({
  renderText = defaultRenderText,
}: CustomQuotedMessagePreviewProps) => {
  const { client } = useChatContext();
  const { Attachment = DefaultAttachment } =
    useComponentContext('QuotedMessagePreview');
  const { userLanguage } = useTranslationContext('QuotedMessagePreview');
  const messageComposer = useMessageComposer();
  const { quotedMessage } = useStateStore(
    messageComposer.state,
    messageComposerStateStoreSelector,
  );
  const {currentUserId} = useAuth();

  const quotedMessageText = useMemo(
    () =>
      quotedMessage?.i18n?.[`${userLanguage}_text` as `${TranslationLanguages}_text`] ||
      quotedMessage?.text,
    [quotedMessage?.i18n, quotedMessage?.text, userLanguage],
  );

  const renderedText = useMemo(
    () => renderText(quotedMessageText, quotedMessage?.mentioned_users),
    [quotedMessage, quotedMessageText, renderText],
  );

  const quotedMessageAttachments = useMemo(
    () =>
      quotedMessage?.attachments?.length ? quotedMessage.attachments.slice(0, 1) : [],
    [quotedMessage],
  );

  const poll = quotedMessage?.poll_id && client.polls.fromState(quotedMessage.poll_id);

  if (!quotedMessageText && !quotedMessageAttachments.length && !poll) return null;

  const quotedMine = quotedMessage?.user?.id == currentUserId;

  return (
    <div
      className={clsx('str-chat__quoted-message-preview', { quotedMine: quotedMine })}
      data-testid='quoted-message-preview'
      style={{
        "--user-color": quotedMine ? 'var(--brand-blue)' : authorColor(quotedMessage?.user?.id ?? '') 
      } as React.CSSProperties}
    >
      <div className='str-chat__quoted-message-bubble'>
        <div className='custom-str-chat__quoted-message-name font-semibold text-sm'>{quotedMessage?.user?.name}</div>
        {poll ? (
          <Poll isQuoted poll={poll} />
        ) : (
          <>
            {!!quotedMessageAttachments.length && (
              <Attachment attachments={quotedMessageAttachments} isQuoted />
            )}
            <div
              className='str-chat__quoted-message-text text-brand-color-2 text-sm'
              data-testid='quoted-message-text'
            >
              {renderedText}
            </div>
          </>
        )}
      </div>
    </div>
  );
};